// src/auth/oidcConfig.js
import { UserManager, WebStorageStateStore } from "oidc-client";

const oidcConfig = {
  authority: process.env.REACT_APP_AUTHORITY,
  client_id: process.env.REACT_APP_CLIENT_ID,
  redirect_uri: process.env.REACT_APP_REDIRECT_URI,
  response_type: "code", // 'code' is the recommended response type for OIDC
  scope: "openid profile amnathrigAPI roles", // The scopes that determine what information is requested from the user
  post_logout_redirect_uri: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI,
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // Silent renewal configuration
  automaticSilentRenew: true,
  silent_redirect_uri: process.env.REACT_APP_SILENT_REDIRECT_URI,
  checkSessionInterval: 60000, // Check session every 60 seconds
  // Session monitoring polls /connect/checksession inside a hidden iframe on
  // this page's own origin. In the CRA dev server (npm start), that's
  // localhost:44401 while the backend/IdentityServer is localhost:5001 — a
  // different origin, so the backend's X-Frame-Options: SAMEORIGIN on that
  // endpoint blocks the framing outright (shows up as a "canceled" request,
  // not an error). In a built/production app the SPA is served by the same
  // backend origin, so this isn't an issue there — only disable it for the
  // dev server.
  monitorSession: process.env.NODE_ENV !== "development",
  accessTokenExpiringNotificationTime: 300, // Notify 5 minutes before expiration

  // Additional token management
  includeIdTokenInSilentRenew: true,
  loadUserInfo: true,

  // Token validation
  clockSkew: 60, // Allow 60 seconds clock skew
};

const userManager = new UserManager(oidcConfig);

// Make userManager available globally for debugging and direct access
window.userManager = userManager;

// Several independent places (ProtectedRoute, apiUtils' 401/opaqueredirect
// handling, axiosConfig's response interceptor, and oidc-client's own
// expiring/silentRenewError events) can each notice an auth failure around
// the same time and each call signinRedirect() on their own. Since that's a
// real top-level page navigation, whichever call wins cancels the others
// mid-flight — showing up as "canceled" requests, not a real failure. Route
// everything through this so only one navigation actually happens.
let redirectPromise = null;
export function redirectToSignIn() {
  if (!redirectPromise) {
    console.log("🔀 Redirecting to sign-in (coordinated, single navigation)...");
    redirectPromise = userManager.signinRedirect().catch((err) => {
      redirectPromise = null; // allow a retry if the navigation itself failed to start
      throw err;
    });
  }
  return redirectPromise;
}

// Set up automatic token renewal event handlers
userManager.events.addAccessTokenExpiring(() => {
  console.log("🔄 Token expiring, attempting silent renewal...");
});

userManager.events.addAccessTokenExpired(() => {
  console.log("❌ Token expired, redirecting to login...");
  redirectToSignIn();
});

userManager.events.addSilentRenewError((error) => {
  console.error("❌ Silent renewal error:", error);
  // Redirect to login if silent renewal fails
  redirectToSignIn();
});

userManager.events.addUserLoaded((user) => {
  console.log("✅ User loaded/refreshed:", user.profile.name);
  console.log(
    "🔑 Token expires at:",
    new Date(user.expires_at * 1000).toLocaleString()
  );
});

userManager.events.addUserSignedIn(() => {
  console.log("✅ User signed in successfully");
});

userManager.events.addUserSignedOut(() => {
  console.log("👋 User signed out");
});

// Add error event handler
userManager.events.addUserSessionChanged(() => {
  console.log("🔄 User session changed");
});

export default userManager;
